import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../hooks/redux';
import { errorHandler } from '../../utils/errorHandler';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireGoals?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireGoals = false,
}) => {
  const { isAuthenticated, isLoading, userStatus, error } = useAppSelector(
    state => state.auth
  );
  const location = useLocation();

  console.log('🔍 ProtectedRoute check:', {
    pathname: location.pathname,
    isAuthenticated,
    isLoading,
    userStatus,
    requireGoals,
    error,
  });

  // Handle authentication errors
  if (error && !isLoading) {
    errorHandler.handleAuthError(error, {
      customMessage: 'Authentication error occurred. Please sign in again.',
      recoveryActions: [
        {
          label: 'Sign In',
          action: () => {
            window.location.href = '/auth';
          },
          primary: true,
        },
        {
          label: 'Go Home',
          action: () => {
            window.location.href = '/';
          },
        },
      ],
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to auth page with return URL
    return (
      <Navigate
        to={`/auth?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  // Handle case where authentication state is still loading
  if (isAuthenticated && userStatus === null) {
    // State is still loading from the backend - this is normal, just show spinner
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // For goal-specific routes, redirect to dashboard if user hasn't completed onboarding
  // This allows them to see goal creation prompts instead of blocking access
  if (
    requireGoals &&
    userStatus !== 'active' &&
    location.pathname !== '/dashboard'
  ) {
    console.log(
      '🔄 Redirecting to dashboard - user needs to complete onboarding for this route'
    );
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to onboarding if user is not active (pending_verification or onboarding)
  if (
    (userStatus === 'pending_verification' || userStatus === 'onboarding') &&
    location.pathname !== '/onboarding'
  ) {
    console.log(`🔄 Redirecting to onboarding - user status: ${userStatus}`);
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};
